import {
    AE_ACTION_TAKEN_OPTIONS,
    AE_CAUSALITY_OPTIONS,
    AE_SEVERITY_OPTIONS,
    AE_STATUS_OPTIONS,
    AE_REPORT_METHOD_OPTIONS,
    AE_OCCURRENCE_OPTIONS,
    AE_OUTCOME_OPTIONS,
    AE_LIKELIHOOD_OPTIONS,
    AE_RELATIONSHIP_OPTIONS,
    AE_PERIOD_OPTIONS,
    AE_DIAGNOSIS_OPTIONS
} from "api/modules/common.option";
import { FormFieldConfig } from "Components/Common/CommonForm";
import { Column } from "Components/Common/CommonTableIndexWise";
import { validations } from "validations/FormValidations";
import React from "react";
import { Progress, UncontrolledTooltip } from "reactstrap";
import { formatDate, parseLocalDate } from "common/service/CommonService";



export const getAdverseEventFields = (
    sectionId: string,
    state: {
        studyOptions: any[];
        subjectsOptions: any[];
        periodOptions?: any[];
        isEditMode?: boolean;
        selectedStudyId?: any;
        isSubjectLoading?: boolean;
        getValues?: any;
        watchedTemperatureUnit?: string;
        watchedTemperatureLocation?: string;
        enrollmentDate?: any;
        watchedOnsetOfEvent?: any;
        watchedIsFollowupRequired?: string;
        isAnyVitalsFieldFilled?: boolean;
    }
): FormFieldConfig[] => {
    const {
        studyOptions,
        subjectsOptions,
        periodOptions,
        isEditMode,
        selectedStudyId,
        isSubjectLoading,
        getValues,
        watchedTemperatureUnit,
        watchedTemperatureLocation,
        enrollmentDate,
        watchedOnsetOfEvent,
        watchedIsFollowupRequired,
        isAnyVitalsFieldFilled,
    } = state;

    switch (sectionId) {
        case "study":
            return [
                {
                    name: "studycode",
                    label: "Study Code",
                    type: "select",
                    required: true,
                    options: studyOptions,
                    disabled: true,
                    rules: validations.adverseEvent.studycode,
                },
                {
                    name: "subjectno",
                    label: "Subject No.",
                    type: "select",
                    required: true,
                    options: subjectsOptions,
                    disabled: !!isEditMode || isSubjectLoading || !selectedStudyId,
                    rules: validations.adverseEvent.subjectno,
                },
                {
                    name: "period",
                    label: "Period",
                    required: true,
                    type: "select",
                    options: periodOptions || AE_PERIOD_OPTIONS,
                    placeholder: "Select Period...",
                    disabled: !!isEditMode,
                },
                {
                    name: "onsetofevent",
                    label: "Onset of Event",
                    required: true,
                    type: "radio",
                    options: [
                        { label: "Pre dose", value: "predose" },
                        { label: "Post dose", value: "postdose" },
                    ],
                },
                {
                    name: "onsetdate",
                    label: "Onset Date",
                    type: "date",
                    required: true,
                    optionsProps: {
                        maxDate: "today",
                    },
                    rules: {
                        ...validations.adverseEvent.onsetdate,
                        validate: (value: string) => {
                            if (!value) return true;
                            if (state.enrollmentDate) {
                                const selectedOnset = parseLocalDate(value);
                                const enrollDate = parseLocalDate(state.enrollmentDate);

                                if (selectedOnset && enrollDate && !isNaN(selectedOnset.getTime()) && !isNaN(enrollDate.getTime())) {
                                    selectedOnset.setHours(0, 0, 0, 0);
                                    enrollDate.setHours(0, 0, 0, 0);
                                    if (selectedOnset < enrollDate) {
                                        const formattedEnrollDate = enrollDate.toLocaleDateString("en-GB");
                                        return `Onset date cannot be before subject enrollment date (${formattedEnrollDate})`;
                                    }
                                }
                            }
                            return true;
                        }
                    },
                },
                {
                    name: "onsettime",
                    label: "Onset Time",
                    type: "time",
                    required: true,
                    rules: validations.adverseEvent.onsettime ? validations.adverseEvent.onsettime(getValues) : undefined,
                },
                {
                    name: "reportingdate",
                    label: "Reporting Date",
                    type: "date",
                    optionsProps: {
                        maxDate: "today",
                    },
                    rules: validations.adverseEvent.reportingdate ? validations.adverseEvent.reportingdate(getValues) : undefined,
                },
                {
                    name: "reportingtime",
                    label: "Reporting Time",
                    type: "time",
                    rules: validations.adverseEvent.reportingtime ? validations.adverseEvent.reportingtime(getValues) : undefined,
                },
            ];

        case "event":
            return [
                {
                    name: "severitygrade",
                    label: "Severity",
                    type: "select",
                    required: true,
                    options: AE_SEVERITY_OPTIONS,
                    rules: validations.adverseEvent.severitygrade,
                },
                {
                    name: "reportmethod",
                    label: "Report Method",
                    type: "select",
                    required: true,
                    options: AE_REPORT_METHOD_OPTIONS,
                },
                {
                    name: "occurrence",
                    label: "Occurrence",
                    type: "select",
                    required: true,
                    options: AE_OCCURRENCE_OPTIONS,
                },
                {
                    name: "lastdosedate",
                    label: "Last Dose Received Date",
                    type: "date",
                    required: watchedOnsetOfEvent === "postdose",
                    optionsProps: {
                        maxDate: "today",
                    },
                    rules: validations.adverseEvent.lastdosedate ? validations.adverseEvent.lastdosedate(getValues) : undefined,
                },
                {
                    name: "lastdosetime",
                    label: "Last Dose Received Time",
                    type: "time",
                    required: watchedOnsetOfEvent === "postdose",
                    rules: validations.adverseEvent.lastdosetime ? validations.adverseEvent.lastdosetime(getValues) : undefined,
                },
                {
                    name: "eventterm",
                    label: "Event Term / Description",
                    required: true,
                    placeholder: "Describe the adverse event...",
                    type: "textarea",
                    rules: validations.adverseEvent.eventterm,
                },
            ];

        case "vitals":
            return [
                {
                    name: "medicalexamstart",
                    label: "Medical Exam Start Time",
                    type: "time",
                    required: isAnyVitalsFieldFilled,
                },
                {
                    name: "medicalexamend",
                    label: "Medical Exam End Time",
                    type: "time",
                    required: isAnyVitalsFieldFilled,
                    rules: validations.adverseEvent.medicalexamend ? validations.adverseEvent.medicalexamend(getValues, isAnyVitalsFieldFilled) : undefined,
                },
                {
                    name: "bloodpressure",
                    label: "Blood Pressure (mm of Hg)",
                    type: "text",
                    placeholder: "e.g. 120/80",
                    rules: validations.adverseEvent.bloodpressure,
                },
                {
                    name: "pulse",
                    label: "Pulse (beats/min)",
                    type: "text",
                    placeholder: "e.g. 72",
                    rules: validations.adverseEvent.pulse,
                },
                {
                    name: "respiratoryrate",
                    label: "Respiratory Rate (breaths/min)",
                    type: "text",
                    placeholder: "e.g. 18",
                    rules: validations.adverseEvent.respiratoryrate,
                },
                {
                    name: "temperatureunit",
                    label: "Temperature Unit",
                    type: "select",
                    options: [
                        { label: "Celsius (°C)", value: "C" },
                        { label: "Fahrenheit (°F)", value: "F" },
                    ],
                    placeholder: "Select Unit...",
                },
                {
                    name: "temperaturelocation",
                    label: "Temperature Location",
                    type: "select",
                    required: !!watchedTemperatureUnit,
                    options: [
                        { label: "Oral", value: "Oral" },
                        { label: "Axillary", value: "Axillary" },
                    ],
                    placeholder: "Select Location...",
                    disabled: !watchedTemperatureUnit,
                },
                {
                    name: "temperature",
                    label: "Temperature Value",
                    type: "number",
                    placeholder: "e.g. 98.6",
                    required: !!watchedTemperatureUnit && !!watchedTemperatureLocation,
                    disabled: !watchedTemperatureUnit || !watchedTemperatureLocation,
                    rules: (watchedTemperatureUnit && watchedTemperatureLocation)
                        ? (typeof validations.adverseEvent.temperature === "function" ? validations.adverseEvent.temperature(getValues) : validations.adverseEvent.temperature)
                        : undefined,
                },
                {
                    name: "associatedsymptoms",
                    label: "Associated with any other symptoms?",
                    type: "radio",
                    options: [
                        { label: "Yes", value: "yes" },
                        { label: "No", value: "no" },
                    ],
                },
                {
                    name: "symptomsremark",
                    required: true,
                    label: "If yes, Remarks",
                    placeholder: "Enter Remarks...",
                    type: "textarea",
                    rules: validations.adverseEvent.symptomsremark,
                },
            ];

        case "examination":
            return [
                {
                    name: "generalexamination",
                    label: "General Examination",
                    type: "radio",
                    required: true,
                    options: [
                        { label: "Normal", value: "normal" },
                        { label: "Abnormal", value: "abnormal" },
                    ],
                },
                {
                    name: "systemicexamination",
                    label: "Systemic Examination",
                    type: "radio",
                    required: true,
                    options: [
                        { label: "Normal", value: "normal" },
                        { label: "Abnormal", value: "abnormal" },
                    ],
                },
                {
                    name: "investigationadvised",
                    label: "Investigation Advised",
                    placeholder: "Describe the Investigation...",
                    type: "textarea",
                    rules: validations.adverseEvent.investigationadvised,
                },

                {
                    name: "diagnosis",
                    label: "Diagnosis",
                    type: "select",
                    required: true,
                    options: AE_DIAGNOSIS_OPTIONS,
                    placeholder: "Select Diagnosis...",
                },
                {
                    name: "diagnosisremarks",
                    label: "Diagnosis Remarks",
                    placeholder: "Describe the Diagnosis...",
                    type: "text",
                    required: (() => {
                        const diagnosisVal = getValues ? getValues("diagnosis") : null;
                        const selectedOption = AE_DIAGNOSIS_OPTIONS.find(opt => Number(opt.value) === Number(diagnosisVal));
                        const label = selectedOption?.label?.toLowerCase() || "";
                        return label === "other" || label.includes("final");
                    })(),
                    rules: validations.adverseEvent.diagnosisremarks ? validations.adverseEvent.diagnosisremarks(getValues) : undefined,
                },
                {
                    name: "actiontaken",
                    required: true,
                    label: "Action Taken",
                    type: "select",
                    options: AE_ACTION_TAKEN_OPTIONS,
                },
                {
                    name: "examremark",
                    label: "Remarks",
                    placeholder: "Enter Remarks...",
                    type: "textarea",
                    rules: validations.adverseEvent.examremark,
                },
                {
                    name: "isfollowuprequired",
                    label: "Is Followup required?",
                    type: "radio",
                    required: true,
                    options: [
                        { label: "Yes", value: "yes" },
                        { label: "No", value: "no" },
                    ],
                },
            ];

        case "clinical":
            return [
                {
                    name: "severitygrade",
                    label: "Severity Grade (CTCAE)",
                    type: "select",
                    required: true,
                    options: AE_SEVERITY_OPTIONS,
                    rules: validations.adverseEvent.severitygrade,
                },
                {
                    name: "causality",
                    label: "Causality (Study Drug)",
                    type: "select",
                    required: true,
                    options: AE_CAUSALITY_OPTIONS,
                    rules: validations.adverseEvent.causality,
                },
                {
                    name: "actiontaken",
                    label: "Action Taken (Study Drug)",
                    type: "select",
                    required: true,
                    options: AE_ACTION_TAKEN_OPTIONS,
                    rules: validations.adverseEvent.actiontaken,
                },
                {
                    name: "otheraction",
                    label: "Other Action Taken",
                    type: "text",
                    rules: validations.adverseEvent.otheraction,
                },
            ];



        case "eventnarrative":
            return [
                {
                    name: "eventnarrative",
                    label: "Event Narrative",
                    type: "textarea",
                    required: true,
                    placeholder:
                        "Describe the sequence of events, actions taken, and current patient status...",
                    rules: validations.adverseEvent.eventnarrative,
                },
            ];

        case "pi_assessment":
            return [
                {
                    name: "finaldiagnosis",
                    label: "Final Diagnosis",
                    type: "text",
                },
                {
                    name: "piseverity",
                    label: "Severity Grade",
                    type: "select",
                    options: AE_SEVERITY_OPTIONS,
                },
                {
                    name: "picausality",
                    label: "Causality",
                    type: "select",
                    options: AE_CAUSALITY_OPTIONS,
                },
                {
                    name: "piactiontaken",
                    label: "Action Taken",
                    type: "select",
                    options: AE_ACTION_TAKEN_OPTIONS,
                },
                {
                    name: "outcomeofevent",
                    label: "Outcome of Event",
                    type: "select",
                    options: AE_OUTCOME_OPTIONS,
                },
                {
                    name: "likelihood",
                    label: "Likelihood",
                    type: "select",
                    options: AE_LIKELIHOOD_OPTIONS,
                },
                {
                    name: "relationship",
                    label: "Relationship",
                    type: "select",
                    options: AE_RELATIONSHIP_OPTIONS,
                },
                {
                    name: "reviewedby",
                    label: "Reviewed By",
                    type: "text",
                },
            ];

        default:
            return [];
    }
};

export const adverseEventColumns: Column[] = [
    { key: "adverseeventnumber", label: "Event ID", width: "140px" },
    { key: "studycode", label: "Study Code", width: "140px" },
    { key: "subjectno", label: "Subject No", width: "130px" },
    {
        key: "eventterm",
        label: "Event Term",
        tooltip: true,
        maxLength: 20,
        render: (_: any, row?: any) => row?.description || row?.eventterm || "—",
    },
    {
        key: "severitygradename",
        label: "Severity Grade",
        width: "160px",
        render: (_: any, row?: any) => row?.severityname || row?.severitygradename || row?.severity || row?.severitygrade || "—",
    },
    { key: "onsetdate", label: "Onset Date", width: "160px" },
    {
        key: "verifiedby",
        label: "Verified By",
        width: "160px",
        render: (_: any, row?: any) => {
            if (!row) return null;
            return (
                <>
                    <p className="mb-0 fw-medium text-dark">{row.verifiedbytext || row.verifiedbyname || "—"}</p>
                    <p className="mb-0 text-muted fs-12">
                        {row.verifieddate ? formatDate(row.verifieddate) : "—"}
                    </p>
                </>
            );
        },
    },
    {
        key: "progress",
        label: "PROGRESS",
        textCenter: true,
        render: (_: any, row?: any) => {
            if (!row) return null;

            const targetId = `ae-progress-${String(row?.id || row?.adverseeventnumber)}`;
            const hasFollowups = Array.isArray(row?.followups)
                ? row.followups.length > 0
                : Boolean(row?.followupcount || row?.totalfollowups || row?.ongoingfollowups || row?.pendingfollowups);

            const followupRequired =
                row?.isfollowuprequired === true ||
                String(row?.isfollowuprequired).toLowerCase() === "yes" ||
                hasFollowups;

            const steps = [
                {
                    title: "Event Details",
                    isCompleted: Boolean(row?.iseventdetailscompleted),
                },
                ...(followupRequired
                    ? [
                        {
                            title: "Follow-ups",
                            isCompleted: Boolean(row?.isfollowupcompleted),
                        },
                    ]
                    : []),
                {
                    title: "Medications",
                    isCompleted: Boolean(row?.ismedicationscompleted),
                },
                {
                    title: "PI Assessment",
                    isCompleted: Boolean(row?.isassessmentcompleted),
                },
                {
                    title: "Verification",
                    isCompleted: Boolean(row?.isverified || row?.isverificationcompleted),
                },
            ];

            const completedCount = steps.filter((s) => s.isCompleted).length;
            const totalCount = steps.length;
            const percentage = Math.round((completedCount / totalCount) * 100);

            let progressColor = "danger";
            if (percentage === 100) {
                progressColor = "success";
            } else if (percentage >= 60) {
                progressColor = "info";
            } else if (percentage >= 33) {
                progressColor = "warning";
            }

            return (
                <>
                    <div
                        id={targetId}
                        className="d-flex flex-column align-items-center justify-content-center mx-auto"
                        style={{ cursor: "pointer", width: "90px" }}
                    >
                        <div
                            className="d-flex align-items-center justify-content-between w-100 mb-1"
                            style={{ fontSize: "10px", lineHeight: 1 }}
                        >
                            <span className="fw-medium text-muted">{completedCount}/{totalCount}</span>
                            <span className={`fw-bold text-${progressColor}`}>{percentage}%</span>
                        </div>
                        <Progress
                            value={percentage}
                            color={progressColor}
                            style={{ height: "6px", width: "100%", borderRadius: "3px" }}
                        />
                    </div>
                    <UncontrolledTooltip
                        placement="left"
                        target={targetId}
                        innerClassName="mw-100 p-2"
                        style={{ pointerEvents: "none", maxWidth: "250px" }}
                    >
                        <div className="d-flex flex-column text-start px-1 py-1" style={{ fontSize: "12px", pointerEvents: "none" }}>
                            {steps.map((step, idx) => (
                                <React.Fragment key={idx}>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <div className="d-flex align-items-center justify-content-center" style={{ width: "18px" }}>
                                            {step.isCompleted ? (
                                                <i className="ri-check-line text-white fw-bold fs-15" />
                                            ) : (
                                                <i className="ri-checkbox-blank-circle-line text-warning fs-13" />
                                            )}
                                        </div>
                                        <span className={step.isCompleted ? "fw-medium text-white" : "text-white-50"} style={{ whiteSpace: "nowrap" }}>
                                            {step.title}
                                        </span>
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <div style={{ width: "18px", height: "14px" }} className="d-flex align-items-center justify-content-start">
                                            <div
                                                style={{
                                                    width: "2px",
                                                    height: "100%",
                                                    backgroundColor: step.isCompleted ? "#20c997" : "rgba(255, 255, 255, 0.4)",
                                                    marginLeft: "8px",
                                                    borderRadius: "1px",
                                                }}
                                            />
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </UncontrolledTooltip>
                </>
            );
        },
    },
];

export const adverseEventStatsCards = (stats: any) => [
    {
        label: "Total Reported AEs",
        value: stats?.totalReported || 0,
        icon: "ri-pulse-line",
        iconBg: "#ecfdf5",
        iconColor: "#10b981",
    },
    {
        label: "Ongoing Follow-ups",
        value: stats?.ongoingFollowups || 0,
        icon: "ri-file-list-3-line",
        iconBg: "#fffbe6",
        iconColor: "#d97706",
    },
    {
        label: "Serious AEs (SAE)",
        value: stats?.seriousAEs || 0,
        icon: "ri-error-warning-line",
        iconBg: "#fef2f2",
        iconColor: "#ef4444",
    },
    {
        label: "Pending Review",
        value: stats?.pendingReview || 0,
        icon: "ri-shield-user-line",
        iconBg: "#eff6ff",
        iconColor: "#3b82f6",
    },
];

export const DEFAULT_FORM_VALUES = {
    studycode: "",
    subjectno: "",
    period: "",
    onsetdate: "",
    onsettime: "",
    reportingdate: "",
    reportingtime: "",
    recordingdate: "",
    recordingtime: "",
    onsetofevent: "",
    eventterm: "",
    severitygrade: "",
    reportmethod: "",
    occurrence: "",
    lastdosedate: "",
    lastdosetime: "",
    medicalexamstart: "",
    medicalexamend: "",
    bloodpressure: "",
    pulse: "",
    respiratoryrate: "",
    temperature: "",
    temperaturelocation: "",
    temperatureunit: "",
    associatedsymptoms: "",
    symptomsremark: "",
    generalexamination: "",
    systemicexamination: "",
    investigationadvised: "",
    examremark: "",
    diagnosis: "",
    diagnosisremarks: "",
    actiontaken: "",
    isfollowuprequired: "",
    followups: [],
    medicines: [],
};

export const mapAdverseEventDataToForm = (data: any, formatToDDMMYYYY: Function) => {
    const onsetVal = data.onsetofevent ? data.onsetofevent.toLowerCase().replace(" ", "") : "";
    const symptomsVal = data.othersymptoms === true ? "yes" : (data.othersymptoms === false ? "no" : (data.associatedsymptoms || ""));

    return {
        studycode: data.studyid ? Number(data.studyid) : "",
        eventterm: data.description || data.eventterm || "",
        period: data.periodno || data.period || "",
        onsetdate: data.onsetdate ? formatToDDMMYYYY(data.onsetdate) : null,
        onsettime: data.onsettime || "",
        reportingdate: data.reportingdate ? formatToDDMMYYYY(data.reportingdate) : null,
        reportingtime: data.reportingtime || "",
        recordingdate: data.recordingdate ? formatToDDMMYYYY(data.recordingdate) : null,
        recordingtime: data.recordingtime || "",
        onsetofevent: onsetVal,
        severitygrade: data.severity || data.severitygrade || "",
        reportmethod: data.reportmethod || "",
        occurrence: data.occurence || data.occurrence || "",
        subjectno: data.subjectno || "",
        lastdosedate: (data.lastdosereceiveddate || data.lastdosedate) ? formatToDDMMYYYY(data.lastdosereceiveddate || data.lastdosedate) : null,
        lastdosetime: data.lastdosereceivedtime || data.lastdosetime || "",
        medicalexamstart: data.medicalexamstarttime || data.medicalexamstart || "",
        medicalexamend: data.medicalexamendtime || data.medicalexamend || "",
        bloodpressure: data.bloodpressure || "",
        pulse: data.pulse || "",
        respiratoryrate: data.respiratoryrate || "",
        temperature: data.temperature || "",
        temperaturelocation: data.temperaturelocation || "",
        temperatureunit: data.temperatureunit || "",
        associatedsymptoms: symptomsVal,
        symptomsremark: data.othersymptomsremark || data.symptomsremark || "",
        generalexamination: data.generalexamination ? data.generalexamination.toLowerCase() : "",
        systemicexamination: data.systemicexamination ? data.systemicexamination.toLowerCase() : "",
        investigationadvised: data.investigationadvised || "",
        examremark: data.remarks || data.examremark || "",
        diagnosis: data.diagnosis || "",
        diagnosisremarks: data.diagnosisremarks || "",
        actiontaken: data.actiontaken || "",
        isfollowuprequired:
            data.isfollowuprequired === true ||
                String(data.isfollowuprequired).toLowerCase() === "yes" ||
                String(data.isfollowuprequired).toLowerCase() === "true"
                ? "yes"
                : data.isfollowuprequired === false ||
                    String(data.isfollowuprequired).toLowerCase() === "no" ||
                    String(data.isfollowuprequired).toLowerCase() === "false"
                    ? "no"
                    : "",
        causality: data.causality || "",
        otheraction: data.otheraction || "",
        eventnarrative: data.eventnarrative || "",
        piseverity: data.piseverity || "",
        picausality: data.picausality || "",
        piactiontaken: data.piactiontaken || "",
        outcomeofevent: data.outcomeofevent || "",
        likelihood: data.likelihood || "",
        relationship: data.relationship || "",
        consumedmedication: data.consumedmedication || "yes",
        reviewedby: data.reviewedby || "",
    };
};

export const mapFollowupsDataToForm = (followups: any[], formatToDDMMYYYY: Function) => {
    const activeFollowups = (followups || []).filter((fu: any) => fu?.status !== "D");
    return activeFollowups.map((fu: any, index: number) => ({
        followupid: fu.followupid,
        adverseeventid: fu.adverseeventid,
        followupstatus: fu.followupstatus || "",
        folloupdate: fu.followupdate ? formatToDDMMYYYY(fu.followupdate) : "",
        starttime: fu.starttime ? fu.starttime.substring(0, 5) : "",
        endtime: fu.endtime ? fu.endtime.substring(0, 5) : "",
        bloodpressure: fu.bloodpressure || "",
        pulse: fu.pulse,
        respiratoryrate: fu.respiratoryrate,
        temperature: fu.temperature,
        temperaturelocation: fu.temperaturelocation || "",
        temperatureunit: fu.temperatureunit || "",
        description: fu.description || "",
        nextfollowuprequired: index < followups.length - 1 ? "yes" : (fu.nextfollowuprequired === true ? "yes" : "no"),
        isSaved: !!(fu.followupdate && fu.starttime),
    }));
};

export const mapMedicationsDataToForm = (data: any, formatToDDMMYYYY: Function) => {
    if (!data) return { resolutiondate: "", resolutiontime: "", consumedmedication: "", medicines: [] };

    let medicinesList: any[] = [];
    let firstRecord: any = {};

    if (Array.isArray(data)) {
        medicinesList = data;
        firstRecord = data[0] || {};
    } else {
        medicinesList = Array.isArray(data.medicines) ? data.medicines : [];
        firstRecord = data;
    }

    const resDate = firstRecord.resolutiondate ? formatToDDMMYYYY(firstRecord.resolutiondate) : "";
    const resTime = firstRecord.resolutiontime ? firstRecord.resolutiontime.substring(0, 5) : "";
    const consumed = firstRecord.medicineconsumed
        ? firstRecord.medicineconsumed.toLowerCase()
        : (firstRecord.consumedmedication || "");

    const activeMedicines = medicinesList.filter((med: any) => med?.status !== "D");

    return {
        resolutiondate: resDate,
        resolutiontime: resTime,
        consumedmedication: consumed,
        medicines: activeMedicines.map((med: any) => ({
            medicineid: med.medicineid,
            brandname: med.brandname || "",
            genericname: med.genericname || "",
            frequency: med.frequency || "",
            route: med.route || "",
            startdate: med.startdate ? formatToDDMMYYYY(med.startdate) : "",
            enddate: med.enddate ? formatToDDMMYYYY(med.enddate) : "",
            interaction: med.interactionwithip || med.interaction || "",
        })),
    };
};

export const formatUpdateAdverseEventPayload = (
    pendingFormData: any,
    id: number,
    user: any,
    reason: string,
    studyOptions: any[],
    DateFormat: Function
) => {
    const selectedStudy = studyOptions.find(
        (s: any) => s.value === Number(pendingFormData.studycode)
    );

    const ensureSeconds = (timeStr: string) => {
        if (!timeStr) return null;
        return timeStr.length === 5 ? `${timeStr}:00` : timeStr;
    };

    return {
        adverseeventid: id,
        updatedby: user?.userid,
        updatereason: reason,

        studyid: pendingFormData.studycode ? Number(pendingFormData.studycode) : null,
        studycode: selectedStudy?.studycode || "",
        subjectno: pendingFormData.subjectno,
        periodno: pendingFormData.period || "",

        onsetdate: DateFormat(pendingFormData.onsetdate) || null,
        onsettime: ensureSeconds(pendingFormData.onsettime),
        reportingdate: DateFormat(pendingFormData.reportingdate) || null,
        reportingtime: ensureSeconds(pendingFormData.reportingtime),
        recordingdate: DateFormat(pendingFormData.recordingdate) || null,
        recordingtime: ensureSeconds(pendingFormData.recordingtime),
        onsetofevent: pendingFormData.onsetofevent === "predose" ? "Pre dose" : "Post dose",

        severity: pendingFormData.severitygrade ? Number(pendingFormData.severitygrade) : null,
        reportmethod: pendingFormData.reportmethod ? Number(pendingFormData.reportmethod) : null,
        occurrence: pendingFormData.occurrence ? Number(pendingFormData.occurrence) : null,
        lastdosereceiveddate: DateFormat(pendingFormData.lastdosedate) || null,
        lastdosereceivedtime: ensureSeconds(pendingFormData.lastdosetime),
        description: pendingFormData.eventterm || "",

        medicalexamstarttime: ensureSeconds(pendingFormData.medicalexamstart),
        medicalexamendtime: ensureSeconds(pendingFormData.medicalexamend),
        bloodpressure: pendingFormData.bloodpressure || "",
        pulse: pendingFormData.pulse ? Number(pendingFormData.pulse) : null,
        respiratoryrate: pendingFormData.respiratoryrate ? Number(pendingFormData.respiratoryrate) : null,
        temperature: pendingFormData.temperature ? Number(pendingFormData.temperature) : null,
        temperaturelocation: pendingFormData.temperaturelocation || null,
        temperatureunit: pendingFormData.temperatureunit || "C",

        othersymptoms: pendingFormData.associatedsymptoms === "yes",
        othersymptomsremark: pendingFormData.symptomsremark || "",

        generalexamination: pendingFormData.generalexamination === "normal" ? "Normal" : "Abnormal",
        systemicexamination: pendingFormData.systemicexamination === "normal" ? "Normal" : "Abnormal",
        investigationadvised: pendingFormData.investigationadvised || "",
        remarks: pendingFormData.examremark || "",
        diagnosis: pendingFormData.diagnosis ? Number(pendingFormData.diagnosis) : null,
        diagnosisremarks: pendingFormData.diagnosisremarks || "",
        actiontaken: pendingFormData.actiontaken ? Number(pendingFormData.actiontaken) : null,
        isfollowuprequired: pendingFormData.isfollowuprequired === "yes",

        followups: pendingFormData.followups,
        medicines: pendingFormData.medicines,
    };
};
